import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout, { canAccessPath } from './components/dashboard/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import LandingPage from './components/landing/LandingPage';
import UpdateBanner from './components/common/UpdateBanner';
import ConfirmHost from './components/common/ConfirmHost';
import TopProgressBar from './components/common/TopProgressBar';
import { SkeletonPage } from './components/common/Skeleton';
import AuthPage from './components/auth/AuthPage';
// Route-level code splitting — heavy pages load on demand (smaller initial bundle, faster first paint).
const OnboardingPage = lazy(() => import('./components/auth/OnboardingPage'));
const DashboardPage = lazy(() => import('./components/dashboard/DashboardPage'));
const ProfilePage = lazy(() => import('./components/dashboard/ProfilePage'));
const OrdersPage = lazy(() => import('./components/orders/OrdersPage'));
const ReturnExchangePage = lazy(() => import('./components/returnExchange/ReturnExchangePage'));
const CustomersPage = lazy(() => import('./components/customers/CustomersPage'));
const StoresPage = lazy(() => import('./components/stores/StoresPage'));
const CouriersIntegrationPage = lazy(() => import('./components/couriers/CouriersIntegrationPage'));
const LocationsPage = lazy(() => import('./components/locations/LocationsPage'));
const ProductsPage = lazy(() => import('./components/products/ProductsPage'));
const AmazonConnectPage = lazy(() => import('./components/stores/AmazonConnectPage')); // A33 — Amazon login URI
const VendorsPage = lazy(() => import('./components/vendors/VendorsPage'));
const PurchaseManagerPage = lazy(() => import('./components/purchaseManager/PurchaseManagerPage'));
const CreditNotePage = lazy(() => import('./components/purchaseManager/CreditNotePage'));
const RtoReconcilePage = lazy(() => import('./components/rtoReconcile/RtoReconcilePage'));
const ScanApp = lazy(() => import('./components/rtoReconcile/ScanApp'));
const PickupsPage = lazy(() => import('./components/pickups/PickupsPage'));
const BulkActionsPage = lazy(() => import('./components/bulkActions/BulkActionsPage'));
const StatusPage = lazy(() => import('./components/status/StatusPage'));   // A88
const ReportsPage = lazy(() => import('./components/reports/ReportsPage'));
// A64 — NDR panel paused 2026-09-11 pending further design. The page, its API and its courier
// adapters are all intact; only the door is closed. To restore: uncomment this and its <Route>
// below, put the nav entry back in Layout.jsx, and set NDR_PANEL_ENABLED=true on the server.
// const NdrPage = lazy(() => import('./components/ndr/NdrPage'));
const PaymentsPage = lazy(() => import('./components/payments/PaymentsPage'));
const CostReconcilePage = lazy(() => import('./components/payments/CostReconcilePage'));
const ModAddonsPage = lazy(() => import('./components/addons/ModAddonsPage'));
const BrandedTrackingPage = lazy(() => import('./components/addons/BrandedTrackingPage'));
const ProfitPage = lazy(() => import('./components/modiq/ProfitPage'));
const ModiqSettingsPage = lazy(() => import('./components/modiq/ModiqSettingsPage'));
const SettingsPage = lazy(() => import('./components/settings/SettingsPage'));
const LabelConfigPage = lazy(() => import('./components/settings/LabelConfigPage'));
const LabelBuilderPage = lazy(() => import('./components/settings/LabelBuilderPage'));
const QCConfigPage = lazy(() => import('./components/settings/QCConfigPage'));
const TeamPage = lazy(() => import('./components/settings/TeamPage')); // A12
const BillingPage = lazy(() => import('./components/billing/BillingPage'));
const AdminLoginPage = lazy(() => import('./components/admin/AdminLoginPage'));
const AdminPanelPage = lazy(() => import('./components/admin/AdminPanelPage'));
const ForceChangePasswordPage = lazy(() => import('./components/auth/ForceChangePasswordPage'));
const AcceptInvitePage = lazy(() => import('./components/auth/AcceptInvitePage')); // A12
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));
const TermsPage = lazy(() => import('./components/legal/TermsPage'));
const DpaPage = lazy(() => import('./components/legal/DpaPage'));
const RefundPage = lazy(() => import('./components/legal/RefundPage'));
const ShippingPage = lazy(() => import('./components/legal/ShippingPage'));
const ContactPage = lazy(() => import('./components/legal/ContactPage'));
const PricingPage = lazy(() => import('./components/legal/PricingPage'));
const TrackingPage = lazy(() => import('./components/tracking/TrackingPage'));

// Dashboard and ModIQ links arrive as /orders?tab=…&attempt=…; OrdersPage reads those from the URL.
const OrdersRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/dashboard/orders', search }} replace />;
};

// Requires logged in + onboarding complete; shows pending overlay if approval pending
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <div style={{ color: '#94a3b8', padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (!user) return <Navigate to="/user" replace />;
  if (user.force_password_change) return <Navigate to="/force-change-password" replace />;
  if (!user.onboarding_complete) return <Navigate to="/onboarding" replace />;
  // A12: hiding the menu entry is not enough on its own — a bookmark, a shared link or a typed URL
  // would still land on a page whose every API call 403s, which looks like a broken app rather than
  // "you don't have access". Send them somewhere that works instead. Owners are unaffected: `access`
  // is absent or all-full for them.
  if (!canAccessPath(pathname, user.access)) return <Navigate to="/dashboard" replace />;
  // A69 — keyed on pathname so a caught error clears when the user navigates away, and so the
  // boundary sits INSIDE Layout: a crashing page loses the page, not the sidebar and nav with it.
  return <Layout><ErrorBoundary key={pathname}>{children}</ErrorBoundary></Layout>;
};

// Only accessible when force_password_change is true
const ForceChangeRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ color: '#94a3b8', padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (!user) return <Navigate to="/user" replace />;
  if (!user.force_password_change) return <Navigate to="/dashboard" replace />;
  return children;
};

// Requires logged in but NOT onboarding complete; if complete → dashboard
const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ color: '#94a3b8', padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (!user) return <Navigate to="/user" replace />;
  if (user.onboarding_complete) return <Navigate to="/dashboard" replace />;
  return children;
};

// Redirects logged-in users away from auth pages
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (!user.onboarding_complete) return <Navigate to="/onboarding" replace />;
    // Return the user to wherever they were kicked out of. Only same-origin paths, so a crafted
    // ?next=https://evil.example can't turn our login into an open redirect.
    const next = new URLSearchParams(window.location.search).get('next');
    const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
    return <Navigate to={safeNext || '/dashboard'} replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <>
    <TopProgressBar />
    <UpdateBanner />
    <ConfirmHost />
    <Suspense fallback={<SkeletonPage />}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/user" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />
      <Route path="/force-change-password" element={<ForceChangeRoute><ForceChangePasswordPage /></ForceChangeRoute>} />
      {/* A12: unauthenticated by nature — the invitee has no login until they finish here. Not
          wrapped in PublicRoute, because that redirects anyone already signed in, and an owner
          testing their own invite flow would otherwise be bounced to the dashboard. */}
      <Route path="/accept-invite" element={<AcceptInvitePage />} />

      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      {/* A82 - ModIQ is its own route now, not a dashboard sub-tab, so it can sit in the left
          menu, be deep-linked and be bookmarked. Same component: the board shares the
          dashboard’s theme, styles and courier-analysis panel, and duplicating those into a
          second page would guarantee they drift apart. */}
      <Route path="/dashboard/modiq" element={<ProtectedRoute><DashboardPage modiqView key="modiq" /></ProtectedRoute>} />
      <Route path="/dashboard/modiq/profit" element={<ProtectedRoute><ProfitPage /></ProtectedRoute>} />
      <Route path="/dashboard/orders" element={<ProtectedRoute><OrdersPage key="orders" /></ProtectedRoute>} />
      {/* Need Attention now lives inside the Orders page (first tab); keep old links working. */}
      <Route path="/dashboard/need-attention" element={<Navigate to="/dashboard/orders" replace />} />
      <Route path="/dashboard/return-exchange" element={<ProtectedRoute><ReturnExchangePage /></ProtectedRoute>} />
      <Route path="/dashboard/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
      <Route path="/dashboard/pickups" element={<ProtectedRoute><PickupsPage /></ProtectedRoute>} />
      <Route path="/dashboard/bulk-actions" element={<ProtectedRoute><BulkActionsPage /></ProtectedRoute>} />
      <Route path="/dashboard/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      {/* A88 — every signed-in person, staff included: not in NAV, so canAccessPath fails open. */}
      <Route path="/dashboard/status" element={<ProtectedRoute><StatusPage /></ProtectedRoute>} />
      {/* A64 — NDR route disabled 2026-09-11 pending further design; see Layout.jsx and
          NDR_PANEL_ENABLED on the server. The page component is kept, not deleted. */}
      <Route path="/dashboard/stores" element={<ProtectedRoute><StoresPage /></ProtectedRoute>} />
      <Route path="/dashboard/couriers" element={<ProtectedRoute><CouriersIntegrationPage /></ProtectedRoute>} />
      <Route path="/dashboard/locations" element={<ProtectedRoute><LocationsPage /></ProtectedRoute>} />
      <Route path="/dashboard/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
      <Route path="/dashboard/vendors" element={<ProtectedRoute><VendorsPage /></ProtectedRoute>} />
      <Route path="/dashboard/purchase-manager" element={<ProtectedRoute><PurchaseManagerPage /></ProtectedRoute>} />
      <Route path="/dashboard/rto-reconcile" element={<ProtectedRoute><RtoReconcilePage /></ProtectedRoute>} />
      <Route path="/dashboard/payments" element={<Navigate to="/dashboard/payments/vendor" replace />} />
      <Route path="/dashboard/payments/vendor" element={<ProtectedRoute><PaymentsPage view="vendor_payments" /></ProtectedRoute>} />
      <Route path="/dashboard/payments/credit-note" element={<ProtectedRoute><CreditNotePage /></ProtectedRoute>} />
      <Route path="/dashboard/payments/remittance" element={<ProtectedRoute><PaymentsPage view="remittance" /></ProtectedRoute>} />
      <Route path="/dashboard/payments/shipping-cost" element={<ProtectedRoute><PaymentsPage view="shipping_cost" /></ProtectedRoute>} />
      <Route path="/dashboard/payments/cost-reconcile" element={<ProtectedRoute><CostReconcilePage /></ProtectedRoute>} />
      <Route path="/dashboard/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/dashboard/mod-addons" element={<ProtectedRoute><ModAddonsPage /></ProtectedRoute>} />
      <Route path="/dashboard/branded-tracking" element={<ProtectedRoute><BrandedTrackingPage /></ProtectedRoute>} />
      {/* A98 - Tracking Analytics now lives on the Dashboard as the Tracking page sub-tab. */}
      <Route path="/dashboard/tracking-analytics" element={<Navigate to="/dashboard?view=tracking" replace />} />
      <Route path="/dashboard/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/dashboard/settings/label" element={<ProtectedRoute><LabelConfigPage /></ProtectedRoute>} />
      <Route path="/dashboard/settings/label/builder" element={<ProtectedRoute><LabelBuilderPage /></ProtectedRoute>} />
      <Route path="/dashboard/settings/label/builder/:id" element={<ProtectedRoute><LabelBuilderPage /></ProtectedRoute>} />
      <Route path="/dashboard/settings/qc" element={<ProtectedRoute><QCConfigPage /></ProtectedRoute>} />
      <Route path="/dashboard/settings/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/dashboard/settings/modiq" element={<ProtectedRoute><ModiqSettingsPage /></ProtectedRoute>} />
      <Route path="/dashboard/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/dpa" element={<DpaPage />} />
      <Route path="/refund" element={<RefundPage />} />
      <Route path="/shipping" element={<ShippingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/tracking/:vid" element={<TrackingPage />} />
      <Route path="/scan" element={<ScanApp />} />

      <Route path="/admnsd" element={<AdminLoginPage />} />
      <Route path="/admnsd/panel" element={<AdminPanelPage />} />

      <Route path="/login" element={<Navigate to="/user" replace />} />
      <Route path="/orders" element={<OrdersRedirect />} />
      <Route path="/stores" element={<Navigate to="/dashboard/stores" replace />} />
      <Route path="/locations" element={<Navigate to="/dashboard/locations" replace />} />
      <Route path="/products" element={<Navigate to="/dashboard/products" replace />} />
      <Route path="/amazon/connect" element={<AmazonConnectPage />} />
      <Route path="/vendors" element={<Navigate to="/dashboard/vendors" replace />} />
      <Route path="/purchase-manager" element={<Navigate to="/dashboard/purchase-manager" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            style: { background: '#ffffff', color: '#0f172a', border: '1px solid #e2e8f0', boxShadow: 'var(--mc-e2)' },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
